<template>
  <section class="reports-chart-section">
    <header class="reports-chart-header">
      <div>
        <h3>{{ chartText.title }}</h3>
        <p>{{ chartText.subtitle }}</p>
      </div>

      <div class="reports-chart-controls">
        <button
          v-if="allowVisibilityToggle"
          type="button"
          class="reports-chart-visibility-btn"
          :aria-label="chartText.visibilityAriaLabel"
          @click="toggleChartVisibility"
        >
          <i class="fa" :class="isChartHidden ? 'fa-eye' : 'fa-eye-slash'" aria-hidden="true"></i>
          <span>{{ isChartHidden ? chartText.showChartButton : chartText.hideChartButton }}</span>
        </button>

        <div
          v-if="!isChartHidden"
          class="reports-chart-mode-switch"
          role="group"
          :aria-label="chartText.modeAriaLabel"
        >
          <button
            type="button"
            class="reports-chart-mode-btn"
            :class="{ active: resolvedMode === 'bar' }"
            @click="updateMode('bar')"
          >
            <i class="fa fa-bar-chart" aria-hidden="true"></i>
            <span>{{ chartText.modeButtons.bar }}</span>
          </button>

          <button
            type="button"
            class="reports-chart-mode-btn"
            :class="{ active: resolvedMode === 'line' }"
            @click="updateMode('line')"
          >
            <i class="fa fa-line-chart" aria-hidden="true"></i>
            <span>{{ chartText.modeButtons.line }}</span>
          </button>

          <button
            type="button"
            class="reports-chart-mode-btn"
            :class="{ active: resolvedMode === 'points' }"
            @click="updateMode('points')"
          >
            <i class="fa fa-circle-o" aria-hidden="true"></i>
            <span>{{ chartText.modeButtons.points }}</span>
          </button>
        </div>
      </div>
    </header>

    <div v-if="!isChartHidden" class="reports-chart-body">
      <apexchart
        :key="chartRenderKey"
        class="reports-chart-canvas"
        :type="chartType"
        :height="262"
        :options="chartOptions"
        :series="chartSeries"
      ></apexchart>
    </div>

    <div
      v-if="hasVariantSelector && !isChartHidden"
      class="reports-chart-variant-section"
    >
      <div class="reports-chart-mode-switch" role="group" :aria-label="chartText.variantAriaLabel">
        <button
          v-for="option in resolvedVariantOptions"
          :key="option.value"
          type="button"
          class="reports-chart-mode-btn"
          :class="{ active: resolvedVariant === option.value }"
          @click="updateVariant(option.value)"
        >
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { REPORTS_SALES_TEXT, requireReportsSalesTextValue } from '@/composables/reports/sales/useReportsSalesPresentation';

export default {
  name: 'ReportsChartSection',
  props: {
    mode: {
      type: String,
      default: 'bar',
    },
    points: {
      type: Array,
      default: () => [],
    },
    seriesData: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: String,
      default: '',
    },
    variantOptions: {
      type: Array,
      default: () => [],
    },
    allowVisibilityToggle: {
      type: Boolean,
      default: false,
    },
    visibilityStorageKey: {
      type: String,
      default: '',
    },
  },
  emits: ['update:mode', 'update:variant'],
  data() {
    return {
      isChartHidden: false,
    };
  },
  created() {
    this.restoreChartVisibility();
  },
  watch: {
    isChartHidden(nextValue) {
      this.persistChartVisibility(nextValue);
    },
  },
  computed: {
    chartText() {
      const text = REPORTS_SALES_TEXT.chartSection;
      requireReportsSalesTextValue(text.title, 'reports.sales.chartSection.title');
      requireReportsSalesTextValue(text.subtitle, 'reports.sales.chartSection.subtitle');
      requireReportsSalesTextValue(text.modeAriaLabel, 'reports.sales.chartSection.modeAriaLabel');
      requireReportsSalesTextValue(text.variantAriaLabel, 'reports.sales.chartSection.variantAriaLabel');
      requireReportsSalesTextValue(text.visibilityAriaLabel, 'reports.sales.chartSection.visibilityAriaLabel');
      requireReportsSalesTextValue(text.defaultSeriesName, 'reports.sales.chartSection.defaultSeriesName');
      requireReportsSalesTextValue(text.hideChartButton, 'reports.sales.chartSection.hideChartButton');
      requireReportsSalesTextValue(text.showChartButton, 'reports.sales.chartSection.showChartButton');
      requireReportsSalesTextValue(text.modeButtons?.bar, 'reports.sales.chartSection.modeButtons.bar');
      requireReportsSalesTextValue(text.modeButtons?.line, 'reports.sales.chartSection.modeButtons.line');
      requireReportsSalesTextValue(text.modeButtons?.points, 'reports.sales.chartSection.modeButtons.points');
      return text;
    },
    resolvedMode() {
      if (this.mode === 'line') {
        return 'line';
      }

      if (this.mode === 'points') {
        return 'points';
      }

      return 'bar';
    },
    normalizedPoints() {
      const points = Array.isArray(this.points) ? this.points : [];

      return points.map((point, index) => {
        const numericValue = Number(point?.value || 0);

        return {
          label: String(point?.label || '').trim() || `P${index + 1}`,
          value: numericValue,
          formattedValue: formatCurrency(numericValue, { currency: 'MXN' }),
        };
      });
    },
    resolvedVariantOptions() {
      return (Array.isArray(this.variantOptions) ? this.variantOptions : [])
        .map((option) => ({
          value: String(option?.value || '').trim(),
          label: String(option?.label || '').trim(),
        }))
        .filter((option) => option.value !== '' && option.label !== '');
    },
    resolvedVariant() {
      const current = String(this.variant || '').trim();
      const matchesCurrent = this.resolvedVariantOptions.some((option) => option.value === current);
      if (matchesCurrent) {
        return current;
      }

      return String(this.resolvedVariantOptions[0]?.value || '').trim();
    },
    hasVariantSelector() {
      return this.resolvedVariantOptions.length > 0;
    },
    normalizedSeriesData() {
      const source = Array.isArray(this.seriesData) ? this.seriesData : [];

      return source
        .map((series, index) => {
          const name = String(series?.name || '').trim() || `Serie ${index + 1}`;
          const rawData = Array.isArray(series?.data) ? series.data : [];

          return {
            name,
            data: rawData.map((value) => Number(value || 0)),
          };
        })
        .filter((series) => Array.isArray(series.data) && series.data.length > 0);
    },
    hasExternalSeries() {
      return this.normalizedSeriesData.length > 0;
    },
    categoryLabels() {
      if (this.hasExternalSeries) {
        const customCategories = Array.isArray(this.categories) ? this.categories : [];
        if (customCategories.length > 0) {
          return customCategories.map((category, index) => {
            const value = String(category || '').trim();
            return value !== '' ? value : `P${index + 1}`;
          });
        }

        const maxLength = this.normalizedSeriesData.reduce((max, series) => Math.max(max, series.data.length), 0);
        return Array.from({ length: maxLength }, (_, index) => `P${index + 1}`);
      }

      return this.normalizedPoints.map((point) => point.label);
    },
    chartType() {
      if (this.resolvedMode === 'points') {
        return 'scatter';
      }

      if (this.resolvedMode === 'line') {
        return 'line';
      }

      return 'bar';
    },
    chartSeries() {
      if (this.hasExternalSeries) {
        if (this.resolvedMode === 'points') {
          return this.normalizedSeriesData.map((series) => ({
            name: series.name,
            data: series.data.map((value, index) => ({
              x: index + 1,
              y: value,
            })),
          }));
        }

        return this.normalizedSeriesData;
      }

      if (this.resolvedMode === 'points') {
        return [{
          name: this.chartText.defaultSeriesName,
          data: this.normalizedPoints.map((point, index) => ({
            x: index + 1,
            y: point.value,
          })),
        }];
      }

      if (this.resolvedMode === 'line') {
        return [{
          name: this.chartText.defaultSeriesName,
          data: this.normalizedPoints.map((point) => point.value),
        }];
      }

      return [{
        name: this.chartText.defaultSeriesName,
        data: this.normalizedPoints.map((point) => point.value),
      }];
    },
    chartOptions() {
      const baseOptions = {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          foreColor: '#64748b',
        },
        colors: ['#0070cd', '#ef4444', '#0ea5e9', '#22c55e'],
        grid: {
          borderColor: 'rgba(153, 152, 153, 0.2)',
          strokeDashArray: 4,
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: this.chartSeries.length > 1,
        },
        yaxis: {
          labels: {
            formatter: (value) => this.formatYAxisValue(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => formatCurrency(Number(value || 0), { currency: 'MXN' }),
          },
        },
      };

      if (this.resolvedMode === 'points') {
        return {
          ...baseOptions,
          markers: {
            size: 7,
            strokeWidth: 2,
            strokeColors: '#ffffff',
            hover: {
              sizeOffset: 2,
            },
          },
          xaxis: {
            min: 1,
            max: Math.max(this.categoryLabels.length, 1),
            tickAmount: Math.max(this.categoryLabels.length - 1, 1),
            labels: {
              formatter: (value) => {
                const index = Math.round(Number(value)) - 1;
                return this.categoryLabels[index] || '';
              },
            },
          },
          tooltip: {
            ...baseOptions.tooltip,
            x: {
              formatter: (value) => {
                const index = Math.round(Number(value)) - 1;
                return this.categoryLabels[index] || '';
              },
            },
          },
        };
      }

      if (this.resolvedMode === 'line') {
        return {
          ...baseOptions,
          stroke: {
            curve: 'smooth',
            width: this.chartSeries.length > 1 ? 3 : 5,
          },
          markers: {
            size: 7,
            strokeWidth: 3,
            strokeColors: '#e6f0fb',
            hover: {
              sizeOffset: 2,
            },
          },
          xaxis: {
            categories: this.categoryLabels,
          },
          fill: {
            type: 'solid',
            opacity: 0.06,
          },
        };
      }

      return {
        ...baseOptions,
        plotOptions: {
          bar: {
            columnWidth: '42%',
            borderRadius: 5,
          },
        },
        xaxis: {
          categories: this.categoryLabels,
        },
      };
    },
    chartRenderKey() {
      const seriesFingerprint = this.chartSeries
        .map((series) => {
          const values = (Array.isArray(series?.data) ? series.data : [])
            .map((point) => {
              if (point && typeof point === 'object') {
                return `${Number(point.x || 0)}:${Number(point.y || 0)}`;
              }

              return Number(point || 0);
            })
            .join(',');

          return `${String(series?.name || '')}:${values}`;
        })
        .join('|');

      return `${this.chartType}-${this.resolvedVariant}-${this.categoryLabels.join('-')}-${seriesFingerprint}`;
    },
  },
  methods: {
    resolveVisibilityStorageKey() {
      if (!this.allowVisibilityToggle) {
        return '';
      }

      return String(this.visibilityStorageKey || '').trim();
    },
    restoreChartVisibility() {
      const key = this.resolveVisibilityStorageKey();
      if (key === '' || typeof window === 'undefined') {
        this.isChartHidden = false;
        return;
      }

      try {
        const storedValue = window.sessionStorage.getItem(key);
        this.isChartHidden = storedValue === '1';
      } catch (error) {
        this.isChartHidden = false;
      }
    },
    persistChartVisibility(nextValue) {
      const key = this.resolveVisibilityStorageKey();
      if (key === '' || typeof window === 'undefined') {
        return;
      }

      try {
        window.sessionStorage.setItem(key, nextValue ? '1' : '0');
      } catch (error) {
        // Ignore storage write failures.
      }
    },
    toggleChartVisibility() {
      this.isChartHidden = !this.isChartHidden;
    },
    formatYAxisValue(value) {
      const numeric = Number(value || 0);
      if (!Number.isFinite(numeric)) {
        return '0.00';
      }

      return numeric.toFixed(2);
    },
    updateMode(nextMode) {
      const normalizedMode = String(nextMode || '').trim().toLowerCase();
      if (normalizedMode !== 'bar' && normalizedMode !== 'line' && normalizedMode !== 'points') {
        return;
      }

      if (normalizedMode === this.resolvedMode) {
        return;
      }

      this.$emit('update:mode', normalizedMode);
    },
    updateVariant(nextVariant) {
      const normalizedVariant = String(nextVariant || '').trim();
      if (normalizedVariant === '' || normalizedVariant === this.resolvedVariant) {
        return;
      }

      this.$emit('update:variant', normalizedVariant);
    },
  },
};
</script>

<style scoped>
.reports-chart-controls {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.reports-chart-visibility-btn {
  border: 1px solid rgba(148, 163, 184, 0.45);
  min-height: 30px;
  border-radius: 7px;
  background: #ffffff;
  color: #334155;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 700;
}

.reports-chart-visibility-btn:hover,
.reports-chart-visibility-btn:focus-visible {
  border-color: #0070cd;
  color: #0f5da8;
}

.reports-chart-variant-section {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

.reports-chart-variant-section .reports-chart-mode-switch {
  flex-wrap: wrap;
}
</style>
